import { useQuery } from '@tanstack/react-query'
import Card, { CardHeader } from '../../../components/ui/Card'
import Spinner from '../../../components/ui/Spinner'
import Badge from '../../../components/ui/Badge'
import { formatCurrency, formatDate, formatTurnaround } from '../../../lib/formatters'
import api from '../../../lib/api'
import type { WorkOrder, Invoice } from '../../../types'

export default function DashboardPage() {
  const { data: kpis, isLoading: kpisLoading } = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard')
      return data?.data || data
    },
  })

  const { data: recentOrders } = useQuery({
    queryKey: ['dashboard', 'recent-orders'],
    queryFn: async () => {
      const { data } = await api.get<{ data: WorkOrder[] }>('/work-orders?limit=5&sort=-createdAt')
      return data.data
    },
  })

  const { data: overdueInvoices } = useQuery({
    queryKey: ['dashboard', 'overdue-invoices'],
    queryFn: async () => {
      const { data } = await api.get<{ data: Invoice[] }>('/invoices?status=overdue&limit=5')
      return data.data
    },
  })

  if (kpisLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  const statCards = [
    {
      label: 'Active Doctors',
      value: kpis?.activeDoctors ?? 0,
      icon: 'fa-user-doctor',
      color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
    },
    {
      label: 'Pending Orders',
      value: kpis?.pendingWorkOrders ?? 0,
      icon: 'fa-clipboard-list',
      color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
    },
    {
      label: 'This Month Revenue',
      value: formatCurrency(kpis?.totalRevenue ?? 0),
      icon: 'fa-indian-rupee-sign',
      color: 'text-green-600 bg-green-100 dark:bg-green-900/30',
    },
    {
      label: 'Outstanding Dues',
      value: formatCurrency(kpis?.outstandingDues ?? 0),
      icon: 'fa-clock',
      color: 'text-red-600 bg-red-100 dark:bg-red-900/30',
    },
  ]

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info' | 'gray'> = {
      completed: 'success',
      delivered: 'success',
      in_progress: 'info',
      received: 'info',
      draft: 'gray',
      cancelled: 'error',
      overdue: 'error',
      paid: 'success',
      partial: 'warning',
      sent: 'info',
    }
    return <Badge variant={variants[status] || 'gray'}>{status ? String(status).replace('_', ' ') : 'unknown'}</Badge>
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Welcome back! Here's what's happening today.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                <i className={`fas ${stat.icon} text-xl`}></i>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-primary-500 to-primary-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100">Completed This Month</p>
              <p className="text-3xl font-bold mt-1">{kpis?.completedThisMonth ?? 0}</p>
            </div>
            <i className="fas fa-check-circle text-4xl text-primary-200"></i>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100">Overdue Invoices</p>
              <p className="text-3xl font-bold mt-1">{kpis?.overdueInvoices ?? 0}</p>
            </div>
            <i className="fas fa-exclamation-triangle text-4xl text-amber-200"></i>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Avg. Turnaround</p>
              <p className="text-3xl font-bold mt-1">{formatTurnaround(kpis?.averageTurnaround ?? 0)}</p>
            </div>
            <i className="fas fa-clock text-4xl text-blue-200"></i>
          </div>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Work Orders */}
        <Card padding="none">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <CardHeader
              title="Recent Work Orders"
              subtitle="Latest orders received"
              action={
                <a href="/work-orders" className="text-sm text-primary-600 hover:text-primary-700">
                  View all <i className="fas fa-arrow-right ml-1"></i>
                </a>
              }
            />
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentOrders?.length ? (
              recentOrders.map((order) => (
                <a
                  key={order.id}
                  href={`/work-orders/${order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{order.displayId}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {order.doctor?.name ?? 'Unknown Doctor'}
                    </p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(order.status)}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Due: {formatDate(order.dueDate)}
                    </p>
                  </div>
                </a>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <i className="fas fa-inbox text-2xl mb-2 opacity-50"></i>
                <p>No recent orders</p>
              </div>
            )}
          </div>
        </Card>

        {/* Overdue Invoices */}
        <Card padding="none">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <CardHeader
              title="Overdue Invoices"
              subtitle="Invoices requiring attention"
              action={
                <a href="/invoices?status=overdue" className="text-sm text-primary-600 hover:text-primary-700">
                  View all <i className="fas fa-arrow-right ml-1"></i>
                </a>
              }
            />
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {overdueInvoices?.length ? (
              overdueInvoices.map((invoice) => (
                <a
                  key={invoice.id}
                  href={`/invoices/${invoice.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{invoice.displayId}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {invoice.doctor?.name ?? 'Unknown Doctor'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">{formatCurrency(invoice.balanceAmount)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Due: {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                </a>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <i className="fas fa-check-circle text-2xl mb-2 text-green-500"></i>
                <p>No overdue invoices!</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
